import { FC } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ReactComponent as ArrowRightIcon } from 'src/assets/svg/arrow-right-line.svg'
import { ReactComponent as PlusIcon } from 'src/assets/svg/plus.svg'
import { REVIEW_ANALYSIS_PLATFORM_DETAILS_ROUTE } from 'src/constants/routes'
import { useReviewAnalysisLinkQuery } from 'src/hooks/useReviewAnalysisDateRange'
import { ReviewsByPlatformItem } from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import { createLink } from 'src/utils/createLink'
import styles from './reviewsByPlatform.module.scss'
import { SentimentChart } from './SentimentChart'
import { getPlatformIcon } from '../../platformIcons'

type RatingLabel = 'Excellent' | 'Good' | 'Poor'

interface PlatformData {
  icon: FC<React.SVGProps<SVGSVGElement>>
  label: RatingLabel
  name: string
  onViewDetails?: () => void
  rating: number
  reviewCount: number
}

const getRatingLabel = (rating: number): RatingLabel => {
  if (rating >= 4.5) return 'Excellent'
  if (rating >= 3.5) return 'Good'
  return 'Poor'
}

const renderStars = (rating: number) => {
  return Array.from({ length: 5 }, (_, i) => {
    const filled = i < Math.floor(rating)
    return (
      <span
        className={styles.star}
        key={i}
        style={{ color: filled ? '#f59e0b' : '#d1d5db' }}
      >
        ★
      </span>
    )
  })
}

const badgeClass = (label: RatingLabel, s: typeof styles) => {
  if (label === 'Excellent') return s.badgeExcellent
  if (label === 'Good') return s.badgeGood
  return s.badgePoor
}

const PlatformCard: FC<PlatformData> = ({
  icon: Icon,
  label,
  name,
  rating,
  reviewCount,
  onViewDetails,
}) => (
  <div className={styles.platformCard}>
    <div className={styles.cardHeader}>
      <Icon className={styles.platformIcon} />
      <p className={styles.platformName}>{name}</p>
    </div>
    <div className={styles.cardBottom}>
      <div className={styles.cardBottomContent}>
        <div className={styles.ratingRow}>
          <span className={styles.ratingValue}>{rating.toFixed(1)}</span>
          <div className={styles.stars}>{renderStars(rating)}</div>
          <span className={`${styles.badge} ${badgeClass(label, styles)}`}>
            {label}
          </span>
        </div>
        <p className={styles.reviewCount}>
          Based on {reviewCount.toLocaleString()} Reviews
        </p>
      </div>
      <button
        className={styles.viewDetailsBtn}
        onClick={onViewDetails}
        type="button"
      >
        View Details <ArrowRightIcon className={styles.arrowIcon} />
      </button>
    </div>
  </div>
)

export type ReviewsByPlatformView = 'chart' | 'reviews'

interface ReviewsByPlatformProps {
  onAddSource?: () => void
  platforms: ReviewsByPlatformItem[]
  view?: ReviewsByPlatformView
}

export const ReviewsByPlatform: FC<ReviewsByPlatformProps> = ({
  onAddSource,
  platforms,
  view = 'reviews',
}) => {
  const navigate = useNavigate()
  const { campaignId = '' } = useParams<{ campaignId: string }>()
  const linkQuery = useReviewAnalysisLinkQuery()

  if (view === 'chart') {
    return <SentimentChart platforms={platforms} />
  }

  return (
    <div className={styles.grid}>
      {platforms.map((platform) => (
        <PlatformCard
          icon={getPlatformIcon(platform.platform)}
          key={platform.name}
          label={getRatingLabel(platform.rating)}
          name={platform.name}
          onViewDetails={() =>
            navigate(
              createLink(REVIEW_ANALYSIS_PLATFORM_DETAILS_ROUTE, {
                params: { campaignId, platformId: platform.platform },
                queryParams: linkQuery,
              })
            )
          }
          rating={platform.rating}
          reviewCount={platform.reviewCount}
        />
      ))}
      <button
        className={styles.addSourceCard}
        onClick={onAddSource}
        type="button"
      >
        Add Source <PlusIcon className={styles.addSourceIcon} />
      </button>
    </div>
  )
}
